import { useMutation, useQueryClient } from '@tanstack/react-query';

import { studentKeys } from '../../student/query-key-factory';
import { DeepPartial } from '../../temporary/deepPartial';
import { useStoreApi } from '../api';
import { storeKeys } from '../query-key-factory';
import { Cart, CartItem, CartResponse } from './cart.types';

const CREATE_CART_MUTATION_KEY = ['cart', 'create'];

type CheckoutCartRequest = {
  cart: DeepPartial<Cart>;

  cart_id: string;
  /**
   * If a cart totals $0, a token is not required when a cart is created at checkout
   */
  token?: string;
  cvv_token?: string;
  /** The cause id to invalidate */
  causeId?: string;
  checkoutType?: 'checkout' | 'pledge';
  recaptcha: string | null;
};

type CheckoutCartResponse = {
  cart: Cart;
};

/** Checkout a generic cart, not tied to any specific item */
const useCheckoutCart = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  const checkoutCart = async ({ checkoutType = 'checkout', ...request }: CheckoutCartRequest) => {
    const { data } = await StoreApi.post<CheckoutCartResponse>(
      `/carts/${request.cart_id}/${checkoutType}`,
      {
        cart: {
          ...request.cart,
          cvv_token: request.cvv_token,
          metadata: {
            ...(request.cart.metadata || {}),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        },
        token: request.token,
        recaptcha_key: request.recaptcha,
      }
    );
 
    return data;
  };
  return useMutation({
    mutationFn: checkoutCart,
    onSuccess: (_data, variables) => {
      const { causeId } = variables;

      void queryClient.invalidateQueries({ queryKey: studentKeys.all });

      if (!causeId) return;
      const invalidationKey = storeKeys.id(causeId).items.all;
      // Since this is a generic donor side mutation, we can invalidate all store items
      return queryClient.invalidateQueries({
        queryKey: invalidationKey,
      });
    },
  });
};

type CreateCartRequest = {
  // If creating a cart with multiple items, the storeItemId is not required
  storeItemId?: string;
  causeId: string;
} & DeepPartial<Cart>;

const useCreateCart = ({
  mutationKey = CREATE_CART_MUTATION_KEY,
}: {
  mutationKey?: string[];
} = {}) => {
  const StoreApi = useStoreApi();

  const createCart = async (cart: CreateCartRequest) => {
    const { causeId, storeItemId, provider, ...rest } = cart;
    const { data } = await StoreApi.post<CartResponse>(`/carts`, {
      cart: {
        store_item_id: storeItemId,
        cause_id: causeId,
        ...rest,
        provider,
      },
    });

    return data.cart;
  };
  return useMutation({
    mutationFn: createCart,
    mutationKey: mutationKey,
  });
};

type CreateCampaignCartRequest = {
  credit_to?: string[];
  credit_write_in?: string[];
  causeId: string;
  campaignId: string;
  status?: string | null;
  cart: DeepPartial<Cart>;
};
type CreateCampaignCartResponse = {
  cart: Cart;
};

const useCreateCampaignCart = () => {
  const StoreApi = useStoreApi();

  const createCart = async (cart: CreateCampaignCartRequest) => {
    const { causeId, campaignId, status } = cart;
    const { data } = await StoreApi.post<CreateCampaignCartResponse>(`/carts`, {
      cart: {
        store_item_id: campaignId,
        cause_id: causeId,
        status,
        ...cart,
      },
    });

    return data.cart;
  };
  return useMutation({
    mutationFn: createCart,
  });
};

type CreateNoteRequest = {
  causeId?: string;
  cartId?: string;
  note: string;
};

const useCreateNote = () => {
  const StoreApi = useStoreApi();

  const createNote = async ({ causeId, cartId, note }: CreateNoteRequest) => {
    if (!causeId || !cartId) {
      return;
    }

    const { data } = await StoreApi.post(`/carts/${cartId}/notes`, {
      cause_id: causeId,
      cart_id: cartId,
      note: {
        text: note,
      },
    });

    return data;
  };

  return useMutation({
    mutationFn: createNote,
  });
};

interface CheckoutCart extends DeepPartial<Cart> {
  credit_to?: CartItem['credit_to'];
  credit_write_in?: CartItem['credit_write_in'];
  store_item_id?: string;
}
interface CheckoutCartItem extends DeepPartial<CartItem> {
  credit_to?: CartItem['credit_to'];
  credit_write_in?: CartItem['credit_write_in'];
  store_item_id: string;
}

type CreateCheckoutCartRequest = {
  cart?: CheckoutCart;
  cart_items?: CheckoutCartItem[];

  storeItemId?: string;
  causeId?: string;
  paymentMethod?: 'cash' | 'check';
  checkNumber?: string | null;
};

const useCheckoutOfflineCart = () => {
  const StoreApi = useStoreApi();

  const queryClient = useQueryClient();

  const createOfflineDonationCart = async (offlineCartRequest: CreateCheckoutCartRequest) => {
    const { causeId, cart_items, cart, paymentMethod, checkNumber } = offlineCartRequest;
    const { data } = await StoreApi.post<CreateCheckoutCartRequest>('/carts/offline/checkout', {
      cart: {
        cause_id: causeId,
        cart_items: cart_items,
        ...cart,
      },
      payment_method: paymentMethod,
      check_number: checkNumber,
    });
    return data.cart;
  };

  return useMutation({
    mutationFn: createOfflineDonationCart,
    onSuccess: (_data, variables) => {
      const { causeId, storeItemId } = variables;
      if (!causeId || !storeItemId) return;
      void queryClient.invalidateQueries({ queryKey: storeKeys.id(causeId).items.all });
    },
  });
};

type ApplyCartDiscountRequest = {
  cartId?: string;
  discountCode?: string;
  causeId?: string;
};

const useApplyCartDiscount = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  const applyCartDiscount = async ({ cartId, discountCode, causeId }: ApplyCartDiscountRequest) => {
    const { data } = await StoreApi.post<CartResponse>(`/carts/${cartId}/discounts`, {
      discount_code: discountCode,
      cause_id: causeId,
    });
    return data.cart;
  };

  return useMutation({
    mutationFn: applyCartDiscount,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: storeKeys.id(variables.causeId).items.all });
    },
  });
};

export {
  useApplyCartDiscount,
  useCheckoutCart,
  useCheckoutOfflineCart,
  useCreateCampaignCart,
  useCreateCart,
  useCreateNote,
};
